// frontend-react/src/components/BoxScore/RankingsModal.js
// Modal to display full league rankings for a stat category

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, TrendingUp } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';

/**
 * RankingsModal Component
 * Shows all teams ranked for a selected stat category
 * Styled to match PlayerProfileModal
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Close handler
 * @param {Object} allRankings - Full rankings object from API (all_rankings)
 * @param {string} yourTeamId - Current user's team ID for highlighting
 * @param {string} defaultCategory - Initial stat category to display
 * @param {string} statsDate - Date for the rankings
 */
const RankingsModal = ({
  isOpen,
  onClose,
  allRankings,
  yourTeamId,
  defaultCategory = 'total_hr',
  statsDate = ''
}) => {
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);

  // Update selectedCategory when defaultCategory changes (when user clicks different tile)
  useEffect(() => {
    if (defaultCategory) {
      setSelectedCategory(defaultCategory);
    }
  }, [defaultCategory]);

  if (!isOpen || !allRankings) return null;

  // Map category keys to display labels
  const categoryLabels = {
    'total_runs': 'Runs',
    'total_hr': 'Home Runs',
    'total_rbi': 'RBI',
    'total_sb': 'Stolen Bases',
    'avg': 'Batting Average',
    'ops': 'OPS',
    'total_wins': 'Wins',
    'total_saves': 'Saves',
    'era': 'ERA',
    'whip': 'WHIP',
    'total_k': 'Strikeouts',
    'total_qs': 'Quality Starts'
  };

  // Format value based on stat type
  const formatValue = (category, value) => {
    if (category === 'avg' || category === 'ops') {
      return formatBattingAvg(value);
    }
    if (category === 'era') {
      return formatERA(value);
    }
    if (category === 'whip') {
      return formatBattingAvg(value);
    }
    return value;
  };

  // Get rankings for selected category
  const rankings = allRankings[selectedCategory] || [];

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/50"
      style={{ zIndex: 999999 }}
      onClick={onClose}
    >
      <div className="relative flex items-center justify-center min-h-screen p-4">
        <div
          className={`
            relative w-full max-w-xs ${dynastyTheme.components.card.base}
            shadow-2xl border border-neutral-600 max-h-[65vh] flex flex-col
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-neutral-700">
            <div className="flex items-center gap-2">
              <TrendingUp className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              <h2 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>
                Rankings
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-1 rounded-lg ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.neutral} transition-colors`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Category Selector */}
          <div className="p-3 border-b border-neutral-700 bg-neutral-800/50">
            <label className={`block text-xs font-medium ${dynastyTheme.classes.text.neutralLight} mb-1.5`}>
              Stat Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`w-full ${dynastyTheme.components.input} px-3 py-2 text-sm`}
            >
              <optgroup label="⚾ Hitting Stats">
                <option value="total_runs">Runs</option>
                <option value="total_hr">Home Runs</option>
                <option value="total_rbi">RBI</option>
                <option value="total_sb">Stolen Bases</option>
                <option value="avg">Batting Average</option>
                <option value="ops">OPS</option>
              </optgroup>
              <optgroup label="🥎 Pitching Stats">
                <option value="total_wins">Wins</option>
                <option value="total_saves">Saves</option>
                <option value="era">ERA</option>
                <option value="whip">WHIP</option>
                <option value="total_k">Strikeouts</option>
                <option value="total_qs">Quality Starts</option>
              </optgroup>
            </select>

            {statsDate && (
              <div className={`text-[10px] ${dynastyTheme.classes.text.neutralLight} mt-1.5 text-center`}>
                {formatDate(statsDate)}
              </div>
            )}
          </div>

          {/* Rankings List - Scrollable */}
          <div className="flex-1 overflow-y-auto p-2.5">
            <div className="space-y-1.5">
              {rankings.map((teamRank) => {
                const isYourTeam = teamRank.team_id === yourTeamId;
                const isFirst = teamRank.rank === 1;
                const isSecond = teamRank.rank === 2;
                const isThird = teamRank.rank === 3;

                return (
                  <div
                    key={teamRank.team_id}
                    className={`flex items-center justify-between p-2 rounded border ${
                      isYourTeam
                        ? `bg-yellow-400/10 border-yellow-400/50`
                        : `bg-neutral-800/60 border-neutral-700`
                    }`}
                  >
                    {/* Rank + Team Name */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {/* Rank Badge */}
                      <div className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                        isFirst
                          ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black'
                          : isSecond
                          ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-black'
                          : isThird
                          ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white'
                          : `bg-neutral-700 ${dynastyTheme.classes.text.neutralLight}`
                      }`}>
                        {teamRank.rank}
                      </div>

                      {/* Team Name */}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${
                          isYourTeam
                            ? dynastyTheme.classes.text.primary
                            : dynastyTheme.classes.text.white
                        }`}>
                          {teamRank.team_name}
                          {isYourTeam && (
                            <span className={`ml-1 text-[10px] font-normal ${dynastyTheme.classes.text.neutralLight}`}>
                              (You)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Value */}
                    <div className={`flex-shrink-0 text-right text-sm font-bold ml-2 ${
                      isYourTeam
                        ? dynastyTheme.classes.text.primary
                        : dynastyTheme.classes.text.white
                    }`}>
                      {formatValue(selectedCategory, teamRank.value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="p-2.5 border-t border-neutral-700 flex justify-end">
            <button
              onClick={onClose}
              className={`px-3 py-1.5 text-sm rounded-lg ${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.darkLighter} transition-colors`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level (same as PlayerProfileModal)
  return ReactDOM.createPortal(modalContent, document.body);
};

export default RankingsModal;
